import type { Category, Discipline, PublicDisplayConfig } from '../../types/domain';
import type { CompetitionPublic, OverallRanking, Result, TeamRanking } from '../../types/results';

const NOW = '2025-05-10T10:00:00Z';

const discipline = (id: number, name: string, sortOrder: number): Discipline => ({
  id,
  categoryId: id >= 100 ? 2 : 1,
  name,
  type: 'POZARNI_UTOK',
  attemptsCount: 2,
  sortOrder,
  plamenOrder: sortOrder,
  timeFormat: 'MM_SS_CC',
  useSecondTimer: true,
});

const category = (id: number, name: string, disciplines: Discipline[]): Category => ({
  id,
  competitionId: 1,
  name,
  ageGroup: id === 1 ? 'MUZI' : 'ZENY',
  sortOrder: id,
  disciplines,
  teamCount: 6,
});

const MUZI_CATEGORY = category(1, 'Muži', [
  discipline(1, 'Požární útok', 1),
  discipline(2, 'Štafeta 4×100', 2),
]);
const ZENY_CATEGORY = category(2, 'Ženy', [
  discipline(101, 'Požární útok', 1),
  discipline(102, 'Štafeta 4×100', 2),
]);

const TEAM_NAMES = [
  'SDH Ostrava-Poruba',
  'SDH Opava',
  'SDH Těškovice',
  'SDH Havířov',
  'SDH Karviná',
  'SDH Frýdek-Místek',
];

const FORMATTED_TIMES = ['0:19,45', '0:19,78', '0:20,12', '0:20,88', '0:21,34', '0:22,01'];
const TIME_MS = [19450, 19780, 20120, 20880, 21340, 22010];

const buildResults = (disciplineId: number, disciplineName: string): Result[] =>
  TEAM_NAMES.map((teamName, i) => ({
    id: disciplineId * 100 + i,
    teamId: i + 1 + (disciplineId >= 100 ? 100 : 0),
    teamName,
    teamStartNumber: i + 1,
    disciplineId,
    disciplineName,
    attemptNumber: 1,
    timeFinalMs: TIME_MS[i],
    timeFinalFormatted: FORMATTED_TIMES[i],
    isNp: false,
    penaltySeconds: i === 2 ? 10 : 0,
    penaltyLane: null,
    rank: i + 1,
    note: null,
    attempts: [
      { lane: 'LP', timerSlot: 1, timeMs: TIME_MS[i], isNp: false, isElectronic: true },
      { lane: 'PP', timerSlot: 2, timeMs: TIME_MS[i] + 120, isNp: false, isElectronic: true },
    ],
    createdAt: NOW,
    updatedAt: NOW,
  }));

const buildOverall = (cat: Category): OverallRanking => {
  const teams: TeamRanking[] = TEAM_NAMES.map((teamName, i) => {
    const perDisciplineRanks: Record<number, number> = {};
    cat.disciplines.forEach((d) => {
      perDisciplineRanks[d.id] = ((i + d.sortOrder) % TEAM_NAMES.length) + 1;
    });
    return {
      teamId: i + 1 + (cat.id === 2 ? 100 : 0),
      teamName,
      startNumber: i + 1,
      totalPoints: (i + 1) * 2,
      finalRank: i + 1,
      perDisciplineRanks,
    };
  });
  return { categoryId: cat.id, categoryName: cat.name, teams };
};

const baseCompetitionFlags = {
  publicResultsLayout: 'CLASSIC' as const,
  publicShowStartNumber: true,
  publicShowPenaltySeconds: true,
  publicShowPoints: true,
  publicShowAttemptDetails: false,
  publicShowLaneDetails: false,
  publicShowNote: false,
  publicRotationSeconds: 10,
  publicRotateOverall: true,
  publicAutoRefreshSeconds: 15,
  publicThemeMode: 'AUTO' as const,
  publicShowLogo: true,
  publicShowOrganizerLogo: false,
  publicOrganizerLogoUrl: null,
  publicOrganizerLogoMime: null,
  publicOrganizerLogoScale: 100,
  boardLayout: 'MATRIX' as const,
  boardThemeMode: 'AUTO' as const,
  boardFontScale: 100,
  boardShowAllTeams: false,
  boardSortMode: 'RANK' as const,
  boardShowLaneDetails: false,
  boardScreensaverEnabled: true,
  boardCustomText: null,
  publicDisclaimerText: null,
  publishAt: null,
};

export const mockPublicCompetition: CompetitionPublic = {
  shortCode: 'UKAZKA',
  name: 'Ukázková soutěž — Těškovice',
  location: 'Těškovice',
  competitionDate: '2025-05-10',
  status: 'LIVE',
  overallRankingVisible: true,
  unitName: 'SDH Těškovice',
  unitSlug: 'teskovice',
  categories: [MUZI_CATEGORY],
  ...baseCompetitionFlags,
};

export const mockBoardCompetition: CompetitionPublic = {
  ...mockPublicCompetition,
  categories: [MUZI_CATEGORY, ZENY_CATEGORY],
};

export const mockResults: Result[] = [
  ...buildResults(1, 'Požární útok'),
  ...buildResults(2, 'Štafeta 4×100'),
  ...buildResults(101, 'Požární útok'),
  ...buildResults(102, 'Štafeta 4×100'),
];

export const mockOverall: OverallRanking[] = [buildOverall(MUZI_CATEGORY), buildOverall(ZENY_CATEGORY)];

export const mockDisplayConfig: PublicDisplayConfig = {
  layout: 'CLASSIC',
  showStartNumber: true,
  showPenaltySeconds: true,
  showPoints: true,
  showAttemptDetails: false,
  showLaneDetails: false,
  showNote: false,
  rotationSeconds: 10,
  rotateOverall: true,
  autoRefreshSeconds: 15,
  themeMode: 'AUTO',
  showLogo: true,
  showOrganizerLogo: false,
  organizerLogoUrl: null,
};

export const MOCK_PUBLIC_URL = 'https://hasicionline.cz/s/UKAZKA';
